// src/components/ai/AIChatMessage.jsx
import React, { useState } from 'react';
import { ClipboardDocumentIcon, CheckIcon, SparklesIcon, DocumentIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';

const AIChatMessage = ({ message }) => {
    const { user } = useAuth();
    const isUser = message.role === 'user';
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(message.content);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const formatContent = (content) => {
        if (!content) return '';
        // Bold **text**
        let formatted = content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        // Italic *text*
        formatted = formatted.replace(/\*(.*?)\*/g, '<em>$1</em>');
        // Code `inline`
        formatted = formatted.replace(/`([^`]+)`/g, '<code class="bg-gray-100 text-purple-700 px-1.5 py-0.5 rounded text-xs font-mono">$1</code>');
        // Newlines
        formatted = formatted.replace(/\n/g, '<br/>');
        return formatted;
    };

    if (isUser) {
        const hasFile = message.content?.startsWith('[File:');
        const fileNameMatch = message.content?.match(/^\[File: (.+?)\]/);
        const fileName = fileNameMatch?.[1];
        const displayContent = hasFile
            ? message.content.replace(/^\[File: .+?\]\n?/, '').trim()
            : message.content;
        const isPdf = fileName?.toLowerCase().endsWith('.pdf');

        return (
            <div className="flex justify-end gap-3 group">
                <div className="max-w-[80%]">
                    {/* Badge file allegato */}
                    {hasFile && fileName && (
                        <div className="mb-1 flex items-center justify-end gap-1.5">
                            <div className="flex items-center gap-1.5 bg-purple-100 text-purple-700 rounded-full px-3 py-1 text-xs font-medium">
                                {isPdf
                                    ? <DocumentIcon className="w-3.5 h-3.5" />
                                    : <PhotoIcon className="w-3.5 h-3.5" />
                                }
                                {fileName}
                            </div>
                        </div>
                    )}
                    <div className="bg-blue-600 text-white rounded-2xl rounded-br-md px-4 py-3 shadow-sm">
                        {displayContent
                            ? <p className="text-sm leading-relaxed">{displayContent}</p>
                            : <p className="text-sm leading-relaxed italic opacity-75">Analizza il file allegato</p>
                        }
                    </div>
                    <p className="text-xs text-gray-400 text-right mt-1">
                        {new Date(message.createdAt).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                </div>
                <div className="w-8 h-8 bg-blue-600 rounded-xl flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-1">
                    {user?.firstName?.[0]}{user?.lastName?.[0]}
                </div>
            </div>
        );
    }

    return (
        <div className="flex gap-3 group">
            <div className="w-8 h-8 bg-gradient-to-br from-purple-600 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0 mt-1 shadow-sm">
                <SparklesIcon className="w-4 h-4 text-white" />
            </div>
            <div className="max-w-[85%]">
                <div className="bg-white rounded-2xl rounded-bl-md px-4 py-3 shadow-sm border border-gray-100">
                    <p
                        className="text-sm text-gray-800 leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: formatContent(message.content) }}
                    />
                </div>
                <div className="flex items-center gap-3 mt-1">
                    <p className="text-xs text-gray-400">
                        {new Date(message.createdAt).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                    <button
                        onClick={handleCopy}
                        className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition opacity-0 group-hover:opacity-100"
                    >
                        {copied
                            ? <><CheckIcon className="w-3 h-3 text-emerald-500" /> Copiato</>
                            : <><ClipboardDocumentIcon className="w-3 h-3" /> Copia</>
                        }
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AIChatMessage;
